import { useState, useCallback, useEffect, Suspense } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import Sidebar from '../components/Sidebar'
import TopBar from '../components/TopBar'
import CommandPalette from '../components/CommandPalette'
import FeedbackWidget from '../components/FeedbackWidget'
import OnboardingTour from '../components/OnboardingTour'
import { VesselProvider } from '../contexts/VesselContext'
import { UnreadCountsProvider } from '../contexts/UnreadCountsContext'
import { useAuth } from '../../core/auth'

// Alt+1 through Alt+9 navigate to the first 9 sidebar pages
const shortcutRoutes = [
  '/lng-ops',                      // Alt+1 → Dashboard
  '/lng-ops/live-view',            // Alt+2 → Live View
  '/lng-ops/voyage-performance',   // Alt+3 → Voyage Performance
  '/lng-ops/vessel-reports',       // Alt+4 → Vessel Reports
  '/lng-ops/graphs',               // Alt+5 → Graphs
  '/lng-ops/reports',              // Alt+6 → Reports
  '/lng-ops/scheduler',            // Alt+7 → Scheduler
]

function PageLoadingFallback() {
  return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-5 w-5 animate-spin text-accent" />
    </div>
  )
}


// ── Main Layout ─────────────────────────────────────────────────────

export default function DashboardLayout() {
  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false)
  const navigate = useNavigate()
  const { user } = useAuth()
  // The demo account is shown to external audiences — hide the feedback widget.
  const showFeedback = user?.email !== 'demo'

  const openPalette = useCallback(() => setCommandPaletteOpen(true), [])
  const closePalette = useCallback(() => setCommandPaletteOpen(false), [])

  // Global Ctrl+K / Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault()
        setCommandPaletteOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  // Alt+1 through Alt+9 keyboard navigation
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Skip when focus is in an input, textarea, or select
      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return

      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
        const num = parseInt(e.key, 10)
        if (num >= 1 && num <= 9) {
          e.preventDefault()
          navigate(shortcutRoutes[num - 1])
        }
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [navigate])

  return (
    <UnreadCountsProvider>
      <div className="flex h-screen overflow-hidden bg-bg-primary">
        <Sidebar />
        <div className="flex flex-1 flex-col overflow-hidden">
          <TopBar onOpenCommandPalette={openPalette} />
          <main className="flex-1 overflow-y-auto p-4">
            <VesselProvider>
              <Suspense fallback={<PageLoadingFallback />}>
                <Outlet />
              </Suspense>
            </VesselProvider>
          </main>
        </div>
        <CommandPalette open={commandPaletteOpen} onClose={closePalette} />
        {showFeedback && <FeedbackWidget />}
        <OnboardingTour />
      </div>
    </UnreadCountsProvider>
  )
}
